import type { Doc } from '@doc';
import { layoutNode, setNodeClassMutation } from '@doc';
import { Point, Rect } from '@geom';
import { type NID, type Nodes } from '@graph';
import type { IString } from '@strings';
import { expectDefined } from '@utils/util';
import { CommandContext } from './CommandContext';
import { revealBounds } from './zoom.commands';

export interface SetNodeTitleResult {
  doc: Doc;
  pan: Point;
  zoom: number;
}

export function setNodeTitle(
  node: NID,
  title: IString,
  context: CommandContext
): SetNodeTitleResult | undefined {
  let { doc } = context;

  if (doc.nodeTitle(node).equals(title)) {
    return;
  }

  const { layouter } = context;

  const oldDoc = doc;

  try {
    doc = doc.produce('setNodeTitle', draft => {
      const doc = draft as Doc;
      doc.setNodeTitle(node, title);
      layoutNode(doc, node, layouter.cache);
    });
    doc = layouter.runIncrementalLayout(doc, oldDoc.bounds);
    const { pan, zoom } = revealBounds(doc.nodeLayout(node).bounds, context);
    const result = { doc, pan, zoom };
    context.update(result, true);
    return result;
  } catch (e) {
    console.error(e);
    return;
  }
}

export interface SetNodesClassResult {
  doc: Doc;
  pan: Point;
  zoom: number;
  changed: Nodes;
}

export function setNodesClass(
  nodes: Nodes,
  newClass: string | undefined,
  context: CommandContext
): SetNodesClassResult | undefined {
  try {
    let { doc } = context;
    const { layouter } = context;
    const oldDoc = doc;
    const changed: Nodes = new Set();

    doc = doc.produce('setNodesClasses', draft => {
      const doc = draft as Doc;
      for (const node of nodes) {
        if (setNodeClassMutation(doc, node, newClass, layouter.cache)) {
          changed.add(node);
        }
      }
    });

    if (changed.size === 0) {
      return;
    }

    doc = layouter.runIncrementalLayout(doc, oldDoc.bounds);

    let changedBounds: Rect | undefined;
    for (const node of changed) {
      changedBounds = doc.nodeLayout(node).bounds.union(changedBounds);
    }

    const { pan, zoom } = revealBounds(expectDefined(changedBounds), context);
    const result = { doc, pan, zoom, changed };
    context.update(result, true);
    return result;
  } catch (e) {
    console.error(e);
    return;
  }
}

export function setSelectedUnitsClass(
  newClass: string | undefined,
  context: CommandContext
): SetNodesClassResult | undefined {
  const { doc, selection } = context;
  const units = doc.unitsIn(selection.nodes);
  if (units.size === 0) {
    return;
  }
  return setNodesClass(units, newClass, context);
}
